import { AddressTemplateField, DEFAULT_ADDRESS_FORMAT_TEMPLATE } from "@/data/models/Settings";

/** The address parts a template can be filled from. Any of them may be missing. */
export interface AddressTemplateValues {
  street?: string | null;
  houseNumber?: string | null;
  zipCode?: string | null;
  city?: string | null;
  country?: string | null;
}

function resolveField(field: AddressTemplateField, values: AddressTemplateValues): string {
  switch (field) {
    case AddressTemplateField.STREET:
      return values.street?.trim() ?? "";
    case AddressTemplateField.HOUSE_NUMBER:
      return values.houseNumber?.trim() ?? "";
    case AddressTemplateField.ZIP_CODE:
      return values.zipCode?.trim() ?? "";
    case AddressTemplateField.CITY:
      return values.city?.trim() ?? "";
    case AddressTemplateField.COUNTRY:
      return values.country?.trim() ?? "";
    case AddressTemplateField.COMMA:
      return ",";
    default:
      return "";
  }
}

/**
 * Writes an address out in the order a template gives.
 *
 * Follows the project-name template's rules, because the two are the same kind of thing and a client
 * configuring both should not meet two behaviours: parts with no value are skipped, the rest are
 * joined with spaces, a space before a comma is collapsed, and a comma left at either end (because
 * the part beside it was empty) is dropped rather than printed.
 *
 * @param fields the configured order; an empty or absent template falls back to
 *   [DEFAULT_ADDRESS_FORMAT_TEMPLATE], so a client that has never opened the setting is unaffected.
 * @param values the address to write out.
 */
export function renderAddressTemplate(
  fields: AddressTemplateField[] | undefined | null,
  values: AddressTemplateValues
): string {
  const template = fields && fields.length > 0 ? fields : DEFAULT_ADDRESS_FORMAT_TEMPLATE;

  const rendered = template
    .map((field) => resolveField(field, values))
    .filter((part) => part !== "")
    .join(" ")
    .replace(/ ,/g, ",")
    // Two commas in a row are what an empty part between them leaves behind.
    .replace(/,\s*,/g, ",")
    .trim();

  return rendered.replace(/^,\s*/, "").replace(/\s*,$/, "").trim();
}

/** The sample address the settings preview is written with, so the preview never depends on real data. */
export const ADDRESS_TEMPLATE_SAMPLE: AddressTemplateValues = {
  street: "Musterstraße",
  houseNumber: "42",
  zipCode: "12345",
  city: "Musterstadt",
  country: "DE",
};
